"use client";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface CommandSidebarListSkeletonProps {
    count?: number;
}

interface CommandAssistantMessageSkeletonProps {
    lines?: number;
    className?: string;
}

interface CommandUserMessageSkeletonProps {
    lines?: 1 | 2;
    className?: string;
}

function CommandUserMessageSkeleton({
    lines = 1,
    className,
}: CommandUserMessageSkeletonProps) {
    return (
        <div className={cn("ml-auto w-fit max-w-[78%]", className)}>
            <div className="space-y-2 rounded-2xl rounded-br-md bg-violet-100/50 px-4 py-3 dark:bg-violet-950/20">
                <Skeleton className="h-3.5 w-full max-w-[12rem] bg-violet-200/80 dark:bg-violet-900/40" />
                {lines === 2 ? (
                    <Skeleton className="h-3.5 w-[72%] max-w-[9rem] bg-violet-200/80 dark:bg-violet-900/40" />
                ) : null}
            </div>
        </div>
    );
}

function CommandAssistantMessageSkeleton({
    lines = 4,
    className,
}: CommandAssistantMessageSkeletonProps) {
    const widths = ["w-[96%]", "w-[88%]", "w-[92%]", "w-[74%]", "w-[62%]"];

    return (
        <div className={cn("min-w-0", className)}>
            <div className="min-w-0 space-y-2.5 pt-1">
                {Array.from({ length: lines }).map((_, index) => (
                    <Skeleton
                        key={index}
                        className={cn("h-3.5", widths[index % widths.length])}
                    />
                ))}
            </div>
        </div>
    );
}

export function CommandSidebarRowSkeleton() {
    return (
        <div className="flex items-center gap-2 rounded-lg px-2 py-1.5">
            <Skeleton className="size-2 shrink-0 rounded-full" />
            <Skeleton className="h-4 flex-1 max-w-[85%]" />
        </div>
    );
}

export function CommandSidebarListSkeleton({ count = 5 }: CommandSidebarListSkeletonProps) {
    return (
        <div className="space-y-0.5">
            {Array.from({ length: count }).map((_, index) => (
                <CommandSidebarRowSkeleton key={index} />
            ))}
        </div>
    );
}

export function CommandProjectFolderSkeleton() {
    return (
        <div className="px-1 py-0.5">
            <Skeleton className="h-7 w-full rounded-lg" />
        </div>
    );
}

export function CommandChatHeaderSkeleton() {
    return (
        <div className="flex items-center gap-3 border-b border-border px-4 py-3 sm:px-6">
            <Skeleton className="size-9 shrink-0 rounded-[9px]" />
            <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-28" />
            </div>
        </div>
    );
}

export function CommandThreadSkeleton() {
    return (
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
            <CommandUserMessageSkeleton lines={1} />
            <CommandAssistantMessageSkeleton lines={3} />
            <CommandUserMessageSkeleton lines={2} className="max-w-[70%]" />
            <CommandAssistantMessageSkeleton lines={5} />
            <CommandUserMessageSkeleton lines={1} className="max-w-[52%]" />
            <CommandAssistantMessageSkeleton lines={4} />
        </div>
    );
}

export function CommandEmptyStatePresetSkeleton() {
    return (
        <ul className="divide-y divide-border/60">
            {Array.from({ length: 6 }).map((_, index) => (
                <li key={index} className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
                    <Skeleton className="size-4 shrink-0 rounded" />
                    <div className="min-w-0 flex-1 space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-40 max-w-[60%]" />
                    </div>
                </li>
            ))}
        </ul>
    );
}

export function CommandComposerSkeleton() {
    return (
        <div className="mx-auto max-w-4xl space-y-2">
            <Skeleton className="h-7 w-36 rounded-full" />
            <Skeleton className="h-[88px] w-full rounded-2xl" />
        </div>
    );
}

export function CommandChatV2ShellSkeleton() {
    return (
        <main className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
            <CommandChatHeaderSkeleton />
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-6 sm:px-6">
                <CommandThreadSkeleton />
            </div>
            <div className="command-composer-region bg-background px-4 py-3 sm:px-6">
                <CommandComposerSkeleton />
            </div>
        </main>
    );
}

export function CommandPageListSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
            <div className="flex items-center gap-3 border-b px-4 py-2.5">
                <Skeleton className="size-4 rounded" />
                <Skeleton className="h-3 w-24" />
            </div>
            <div className="divide-y divide-border/60">
                {Array.from({ length: count }).map((_, index) => (
                    <div key={index} className="flex items-center gap-3 px-4 py-3">
                        <Skeleton className="size-4 shrink-0 rounded" />
                        <Skeleton className="h-4 flex-1 max-w-[60%]" />
                        <Skeleton className="h-3 w-16 shrink-0" />
                    </div>
                ))}
            </div>
        </div>
    );
}

function CommandV2AgentCardSkeleton() {
    return (
        <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-start gap-3">
                <Skeleton className="size-10 shrink-0 rounded-[9px]" />
                <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/5" />
                    <Skeleton className="h-3 w-2/5" />
                </div>
                <Skeleton className="size-7 shrink-0 rounded-md" />
            </div>
            <div className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-[88%]" />
            </div>
            <div className="space-y-3 border-t border-border/60 pt-3">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-7 w-[132px] rounded-lg" />
                </div>
            </div>
        </div>
    );
}

export function CommandV2AgentGridSkeleton({ count = 6 }: { count?: number }) {
    return (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 min-[1720px]:grid-cols-4">
            {Array.from({ length: count }).map((_, index) => (
                <CommandV2AgentCardSkeleton key={index} />
            ))}
        </div>
    );
}

function CommandFilesTableRowSkeleton({ index }: { index: number }) {
    const nameWidths = ["w-[72%]", "w-[58%]", "w-[80%]", "w-[64%]", "w-[76%]", "w-[55%]", "w-[68%]", "w-[62%]"];

    return (
        <tr className="border-b last:border-b-0">
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <Skeleton className="size-4 shrink-0 rounded" />
                    <Skeleton className={cn("h-4", nameWidths[index % nameWidths.length])} />
                </div>
            </td>
            <td className="px-4 py-3">
                <Skeleton className="h-5 w-10 rounded-md" />
            </td>
            <td className="px-4 py-3">
                <Skeleton className="h-5 w-[5.5rem] rounded-full" />
            </td>
            <td className="px-4 py-3">
                <Skeleton className="h-4 w-20" />
            </td>
            <td className="px-4 py-3">
                <Skeleton className="h-4 w-14" />
            </td>
            <td className="px-4 py-3">
                <Skeleton className="h-4 w-12" />
            </td>
            <td className="px-4 py-3 text-right">
                <Skeleton className="ml-auto size-7 rounded-full" />
            </td>
        </tr>
    );
}

export function CommandFilesTableSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b bg-muted/50 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            <th className="px-4 py-2.5">Name</th>
                            <th className="px-4 py-2.5">Type</th>
                            <th className="px-4 py-2.5">Source</th>
                            <th className="px-4 py-2.5">Chat</th>
                            <th className="px-4 py-2.5">Size</th>
                            <th className="px-4 py-2.5">Created</th>
                            <th className="w-12 px-4 py-2.5" />
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: count }).map((_, index) => (
                            <CommandFilesTableRowSkeleton key={index} index={index} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
